import { useState } from "react";
import { useAuth } from "wasp/client/auth";
import {
  assignTradovateConnectionPropFirm,
  disconnectTradovate,
  getPropFirmTemplates,
  getTradovateConnections,
  reconnectTradovate,
  useQuery,
} from "wasp/client/operations";
import { FlaskConical, Loader2, Lock, Plus, RefreshCw, Unplug } from "lucide-react";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";
import { useToast } from "../../../client/hooks/use-toast";
import { userHasBrokerSync } from "../../../payment/plans";
import TradovateConnectionBeamCard from "../../../trading-accounts/tradovate/TradovateConnectionBeamCard";
import TradovateSyncStatus from "../../../trading-accounts/tradovate/TradovateSyncStatus";
import TradovateDemoSyncStatus from "../../../trading-accounts/tradovate/TradovateDemoSyncStatus";
import TradovateImportReview from "../../../trading-accounts/tradovate/TradovateImportReview";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../client/components/ui/dialog";
import { resolveSyncPresentation } from "../../../trading-accounts/tradovate/tradovateSyncPresentation";
import { resolveSyncActions } from "../../../trading-accounts/tradovate/tradovateSyncActions";
import { resolvePropFirmLogo } from "../../../trading-accounts/tradovate/tradovatePropFirmLogo";
import { filterTradovatePropFirms } from "../../../trading-accounts/tradovate/tradovatePropFirms";
import { DEMO_CONNECTIONS, isSyncDemoEnabled } from "../../../trading-accounts/tradovate/tradovateSyncDemoData";
import { useLiteData } from "../../data/LiteDataProvider";
import { Select } from "../../ui/Select";
import { cardCls, liteFirmLogo } from "../../ui/ui";

/**
 * Synchro Tradovate, version Lite : la même carte à faisceaux que l'Advanced
 * (TradovateConnectionBeamCard, variante `sober`), vers les comptes ACTIFS de la
 * prop firm. Seules les actions changent : ajouter et déconnecter en icônes dans le
 * coin, « Reconnecter » en pied seulement quand il faut ré-autoriser, sans ligne de
 * fraîcheur.
 */
export function LiteTradovateSync({ onConnectNewPropFirm, onAddAccountToConnection }: {
  onConnectNewPropFirm: () => void;
  onAddAccountToConnection: (connectionId: string) => void;
}) {
  const t = useTranslation().tradovate;
  const { data: user } = useAuth();
  const { rawAccounts } = useLiteData();
  // `{}` exactement : même entrée de cache que le reste de l'app.
  const { data: connections = [] } = useQuery(getTradovateConnections, {});
  // Aperçu avec des connexions simulées : `?syncDemo=1` ET build de développement
  // (même garde que la section Advanced). Impossible à déclencher en prod.
  const isDemo = isSyncDemoEnabled(typeof window === "undefined" ? "" : window.location.search, import.meta.env.DEV);
  const conns = (isDemo ? DEMO_CONNECTIONS : connections) as any[];
  // Démo : le « + » ouvre la vraie revue d'import, alimentée par des comptes simulés.
  const [demoImport, setDemoImport] = useState<any | null>(null);
  const { data: templates = [] } = useQuery(getPropFirmTemplates, undefined, { enabled: isDemo });
  if (!conns.length) return null;
  const canConnect = !!user && userHasBrokerSync(user as any);

  return (
    <section className={`${cardCls} p-4`}>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">{t.sectionTitle}</h2>
          <span className="text-xs text-muted-foreground">{t.sectionFirmCount.replace("{count}", String(conns.length))}</span>
        </div>
        {canConnect ? (
          <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" onClick={isDemo ? undefined : onConnectNewPropFirm}>
            <Plus className="mr-1 h-3.5 w-3.5" />{t.connectNewPropFirm}
          </Button>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />{t.maxPlanRequired}</span>
        )}
      </header>

      {isDemo && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-warning/10 px-3 py-2">
          <FlaskConical className="h-4 w-4 shrink-0 text-warning" />
          <span className="text-xs font-semibold text-warning">{t.demoBannerTitle}</span>
          <span className="text-xs text-muted-foreground">{t.demoBannerHint}</span>
        </div>
      )}

      {/* Même grille que la section Advanced : la largeur de carte décide du nombre de colonnes. */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(max(min(100%,420px),calc((100%_-_2.25rem)/4)),1fr))] gap-3">
        {conns.map(conn => {
          const firm = conn.propFirmTemplate;
          const logo = firm ? liteFirmLogo(firm) : resolvePropFirmLogo(null);
          const accounts = isDemo
            ? (conn.demoAccounts as { id: string; name: string }[]).map(a => ({ id: a.id, label: a.name }))
            : rawAccounts
              .filter(a => (a as any).tradovateSyncedAccount?.connectionId === conn.id && a.isActive)
              .map(a => ({ id: String(a.id), label: (a as any).tradovateSyncedAccount?.tradovateAccountName || a.name || String(a.id) }));
          // En démo, aucune action réseau : les connexions n'existent pas côté serveur.
          const addAccount = () => (isDemo ? setDemoImport(conn) : onAddAccountToConnection(conn.id));
          return (
            <TradovateConnectionBeamCard
              key={conn.id}
              sober
              firmName={firm?.name ?? t.connectionBannerTitle}
              logoSrc={logo.src}
              logoColor={logo.color}
              environment={conn.environment}
              accounts={accounts}
              onAddAccount={addAccount}
              syncTone={resolveSyncPresentation({ displayStatus: conn.displayStatus, realtimeStatus: conn.realtimeStatus }).tone}
              footerStatus={isDemo
                ? <TradovateDemoSyncStatus displayStatus={conn.displayStatus} realtimeStatus={conn.realtimeStatus} lastSyncAt={conn.lastSyncAt} part="badge" showFreshness={false} />
                : <TradovateSyncStatus connectionId={conn.id} part="badge" showFreshness={false} />}
              footerActions={<ReconnectAction conn={conn} demo={isDemo} />}
              cornerActions={<CornerActions conn={conn} demo={isDemo} onAddAccount={addAccount} />}
              assignSlot={!firm && !isDemo ? <AssignFirm connectionId={conn.id} /> : undefined}
            />
          );
        })}
      </div>

      {isDemo && (
        <Dialog open={!!demoImport} onOpenChange={v => !v && setDemoImport(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {demoImport && (
              <TradovateImportReview
                connectionId={demoImport.id}
                // Le vrai modèle de la prop firm (même slug), pour proposer ses paliers.
                propFirmTemplateId={(templates as any[]).find(tpl => tpl.slug === demoImport.propFirmTemplate?.slug)?.id ?? null}
                demoAccounts={demoImportAccounts(demoImport)}
                onDone={() => setDemoImport(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

/** Comptes « découverts » simulés : deux nouveaux et un déjà connu d'Edgely. */
function demoImportAccounts(conn: any) {
  const prefix = String(conn.demoAccounts?.[0]?.name ?? "DEMO").replace(/\d+$/, "") || "DEMO";
  return [
    { tradovateAccountId: 9_000_001, name: `${prefix}0000101`, apiEnv: "demo" as const, balance: 50_000, suggestedTierId: null, confidence: "none" as const, isKnown: false },
    { tradovateAccountId: 9_000_002, name: `${prefix}0000102`, apiEnv: "demo" as const, balance: 49_212.5, suggestedTierId: null, confidence: "none" as const, isKnown: false },
    { tradovateAccountId: 9_000_003, name: `PA${prefix}0000099`, apiEnv: "demo" as const, balance: 51_340, suggestedTierId: null, confidence: "none" as const, isKnown: true },
  ];
}

/** « Reconnecter », seulement quand la règle de l'app le demande (resolveSyncActions). */
function ReconnectAction({ conn, demo }: { conn: any; demo: boolean }) {
  const t = useTranslation().tradovate;
  const tl = useTranslation().lite.accounts;
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const status = conn.displayStatus ?? conn.status ?? "active";
  if (!resolveSyncActions({ displayStatus: status, realtimeStatus: conn.realtimeStatus ?? "not_enabled" }).reconnect) return null;
  const reconnect = async () => {
    if (demo) { toast({ title: tl.demo.reconnectTitle, description: tl.demo.reconnectText }); return; }
    setBusy(true);
    try {
      const { url } = await reconnectTradovate({ connectionId: conn.id });
      window.location.href = url;
    } catch (err: any) {
      toast({ title: t.errorConnectionFailed, description: err?.message || undefined, variant: "destructive" });
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" className={`h-8 px-2 text-xs ${status === "needs_reauth" || status === "foreign_token" ? "text-warning" : "text-muted-foreground"}`} onClick={reconnect} disabled={busy}>
      <RefreshCw className="mr-1 h-3.5 w-3.5" />{t.reconnect}
    </Button>
  );
}

/** Ajouter un compte et déconnecter, en icônes dans le coin de la carte. */
function CornerActions({ conn, demo, onAddAccount }: { conn: any; demo: boolean; onAddAccount: () => void }) {
  const t = useTranslation().tradovate;
  const tl = useTranslation().lite;
  const { toast } = useToast();
  const { refetch } = useQuery(getTradovateConnections, {});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const actions = resolveSyncActions({ displayStatus: conn.displayStatus ?? conn.status ?? "active", realtimeStatus: conn.realtimeStatus ?? "not_enabled" });
  const firmName = conn.propFirmTemplate?.name ?? t.connectionBannerTitle;

  const disconnect = async () => {
    if (demo) {
      setConfirming(false);
      toast({ title: tl.accounts.demo.disconnectTitle, description: tl.accounts.demo.noAction });
      return;
    }
    setBusy(true);
    try {
      await disconnectTradovate({ connectionId: conn.id });
      setConfirming(false);
      refetch();
    } catch (err: any) {
      toast({ title: t.errorGeneric, description: err?.message || undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const icon = "h-8 w-8 p-0 text-muted-foreground hover:text-foreground";
  return (
    <>
      <Button size="sm" variant="ghost" className={icon} onClick={onAddAccount} title={t.addAccount} aria-label={t.addAccount}>
        <Plus className="h-4 w-4" />
      </Button>
      {(actions.disconnect || actions.overflowDisconnect) && (
        <Button size="sm" variant="ghost" className={`${icon} hover:text-destructive`} onClick={() => setConfirming(true)} title={t.disconnect} aria-label={t.disconnect}>
          <Unplug className="h-4 w-4" />
        </Button>
      )}
      <Dialog open={confirming} onOpenChange={v => !busy && setConfirming(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{tl.accounts.disconnect.title(firmName)}</DialogTitle>
            <DialogDescription>{tl.accounts.disconnect.text}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>{tl.common.cancel}</Button>
            <Button variant="destructive" onClick={disconnect} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unplug className="mr-2 h-4 w-4" />}{t.disconnect}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Connexion pas encore rattachée à une prop firm : même action que l'Advanced. */
function AssignFirm({ connectionId }: { connectionId: string }) {
  const tt = useTranslation().tradovate;
  const { toast } = useToast();
  const { refetchAll } = useLiteData();
  const { data: templates = [] } = useQuery(getPropFirmTemplates);
  const { refetch } = useQuery(getTradovateConnections, {});
  const [busy, setBusy] = useState(false);
  const assign = async (propFirmTemplateId: string | null) => {
    if (!propFirmTemplateId) return;
    setBusy(true);
    try {
      await assignTradovateConnectionPropFirm({ connectionId, propFirmTemplateId });
      refetch(); refetchAll();
    } catch (err: any) {
      toast({ title: tt.errorGeneric, description: err?.message || undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5" title={tt.assignPropFirmHint}>
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      <Select<string>
        label={tt.assignPropFirm}
        allLabel={tt.assignPropFirm}
        value={null}
        options={filterTradovatePropFirms(templates as any[]).map((tpl: any) => ({ value: tpl.id, label: tpl.name }))}
        onChange={assign}
      />
    </span>
  );
}
